/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import md5 from "blueimp-md5";
import marked, { Token } from "marked";
import BaseLogic from "../../utils/BaseLogic";
import { LocalizedPolicyInformation, LocalizedPolicy } from "../../api/legal/models/policies";
import { fetchDocument } from "../../api/s3/S3";

interface Props {
  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation;
}

interface State {
  isFetchingData: boolean;
  isGeneratingHTML: boolean;
  document: string | null;
  htmlString: string | null;
  headings: marked.Tokens.Heading[] | null;
  headerPrefix: string;
  error: Error | null;
}

export class DocumentTypeError extends Error {
  constructor(m?: string) {
    super(m);
    Object.setPrototypeOf(this, DocumentTypeError.prototype);
  }
}
export class DocumentSignatureError extends Error {
  constructor(m?: string) {
    super(m);
    Object.setPrototypeOf(this, DocumentSignatureError.prototype);
  }
}

const DOCUMENT_HEADER_PREFIX = "section-";
const HEADING_LEVEL_DEPTH_TO_DISPLAY = 2;

export default class MarkdownDocumentLogic extends BaseLogic<State> {
  private readonly props: Props;

  constructor(props: Props) {
    super();
    this.props = props;
    this.state = {
      isFetchingData: false,
      isGeneratingHTML: false,
      document: null,
      htmlString: null,
      headings: null,
      headerPrefix: DOCUMENT_HEADER_PREFIX,
      error: null,
    };
    marked.setOptions({
      headerPrefix: DOCUMENT_HEADER_PREFIX,
    });
  }

  init() {
    this.fetchMarkdownDocument();
  }

  async fetchMarkdownDocument() {
    const { localizedPolicy } = this.props;
    const { isFetchingData } = this.state;
    if (isFetchingData) return;
    await this.setState({ isFetchingData: true });
    try {
      if (
        localizedPolicy.contentType !== "text/markdown" ||
        !localizedPolicy.attachmentLocation ||
        !localizedPolicy.attachmentChecksum
      )
        throw new DocumentTypeError();

      const documentData = await fetchDocument(localizedPolicy.attachmentLocation);

      if (!!localizedPolicy.attachmentChecksum) {
        const checksum = md5(documentData.data);
        if (checksum !== localizedPolicy.attachmentChecksum) throw new DocumentSignatureError();
      }

      await this.setState({ document: documentData.data });
      await this.generateHtmlString();
      await this.generateHeadings();
    } catch (error) {
      await this.setState({ error });
    } finally {
      await this.setState({ isFetchingData: false });
    }
  }

  async generateHtmlString() {
    const { document, isGeneratingHTML } = this.state;
    if (!document || isGeneratingHTML) return;

    await this.setState({ isGeneratingHTML: true });

    const htmlString = marked(document);

    await this.setState({
      htmlString,
      isGeneratingHTML: false,
    });
  }

  async generateHeadings() {
    const { document } = this.state;
    if (!document) return;
    const allHeadings = marked
      .lexer(document)
      .filter((element: Token) => element.type === "heading") as marked.Tokens.Heading[];

    const uniqueDepth = allHeadings
      .map((heading) => heading.depth)
      .filter((value, index, self) => self.indexOf(value) === index);

    const filteredHeading = allHeadings
      .map((heading) => ({
        ...heading,
        depth: uniqueDepth.indexOf(heading.depth),
      }))
      .filter((heading) => heading.depth < HEADING_LEVEL_DEPTH_TO_DISPLAY);

    await this.setState({
      headings: filteredHeading,
    });
  }
}
